import { useEffect, useState, type JSX } from 'react';
import type {
  Link,
  MapLayer,
  MonitorState,
  NetworkNode,
  NodeAddress,
  NodeGroup,
} from '@netdeck/contracts';
import { useTranslation } from '../../shared/i18n';
import { NODE_KIND_LABEL_KEYS, NodeIcon } from '../../shared/ui/NodeIcon';
import { StatusDot, type Status } from '../../shared/ui/StatusDot';
import type { NodeVncAccess } from '../vnc/access';
import { VncSection } from '../vnc/VncSection';
import type { NodeSshAccess } from '../ssh/node-access';
import { SshSection } from '../ssh/SshSection';
import type { NodeVideoAccess } from '../video/access';
import { CameraSection } from '../video/CameraSection';
import { RecorderSection } from '../video/RecorderSection';
import { AddressesSection } from './AddressesSection';
import { MonitorsSection } from './MonitorsSection';
import { NodeProperties } from './NodeProperties';
import { useUpdateNode } from './api';

/** Разделы панели, которые спорят за место: раскрыт не больше одного. */
type PanelSection = 'vnc' | 'ssh' | 'camera' | 'recorder';

/**
 * Правая панель экрана узлов — свойства выбранного узла и его мониторы.
 *
 * Раздел 03 макета отводит эту панель карте сети (фаза 4). Сделана здесь
 * потому, что без неё узел можно только создать и удалить: опечатка в адресе
 * означала бы удаление узла вместе с его историей проверок.
 */
export function NodePanel({
  node,
  groups,
  addresses,
  layers,
  hiddenLayers,
  links,
  status,
  live,
  canWrite,
  vnc,
  ssh,
  video,
}: {
  node: NetworkNode;
  groups: NodeGroup[];
  /** Дополнительные адреса узла из схемы площадки (ADR 035). */
  addresses: readonly NodeAddress[];
  /** Слои площадки. Не переданы — узел показывается без слоя: см. NodeProperties. */
  layers?: readonly MapLayer[];
  /** Слои, погашенные зрителем: переезд на такой слой уводит узел с экрана. */
  hiddenLayers?: ReadonlySet<string>;
  /**
   * Связи площадки. Нужны, чтобы предупредить перед тем, как убрать точку
   * крепления: без них форма не знает, что удалит сервер.
   */
  links?: readonly Link[];
  status: Status;
  live: Map<string, MonitorState>;
  canWrite: boolean;
  /** Доступ к VNC. Не передан — раздела VNC в панели нет. */
  vnc?: NodeVncAccess;
  ssh?: NodeSshAccess;
  /** Доступ к видео. Не передан — раздела камеры в панели нет. */
  video?: NodeVideoAccess;
}): JSX.Element {
  const { t } = useTranslation();

  const updateNode = useUpdateNode(node.siteId);

  /*
    Раскрыт может быть только один раздел настроек. Открытые разом VNC и SSH
    давали панель в два-три экрана прокрутки — у каждого внутри сетка полей,
    учётные данные и зондирование, — а на телефоне, где панель приходит
    шторкой, и того длиннее. Знание общее на панель, поэтому и живёт здесь,
    а не в каждом разделе своим `useState`, как было.
  */
  const [openSection, setOpenSection] = useState<PanelSection | null>(null);

  /*
    Смена узла закрывает всё — тем же приёмом, что правка в `NodeProperties`:
    панель при переходе к соседнему узлу не пересоздаётся, и раскрытый раздел
    остался бы раскрытым, показывая настройки прежней машины, пока грузятся
    настройки новой.
  */
  useEffect(() => setOpenSection(null), [node.id]);

  const toggle = (section: PanelSection) => (): void =>
    setOpenSection((current) => (current === section ? null : section));

  return (
    <aside className="flex min-h-0 flex-col overflow-auto">
      <header className="flex h-11 flex-none items-center gap-2.5 border-b border-line bg-raised px-3.5">
        <StatusDot status={status} />
        <h3 className="truncate text-lg font-semibold">{node.name}</h3>
        <span className="flex items-center gap-1.5 font-mono text-xs text-dim">
          <NodeIcon kind={node.kind} />
          {t(NODE_KIND_LABEL_KEYS[node.kind])}
        </span>
      </header>

      <NodeProperties
        node={node}
        groups={groups}
        layers={layers}
        hiddenLayers={hiddenLayers}
        links={links}
        canWrite={canWrite}
        pending={updateNode.isPending}
        error={updateNode.error}
        // `mutateAsync`, а не `mutate`: форме нужно знать исход, чтобы закрыться
        // только после удачного сохранения.
        onSave={(input) => updateNode.mutateAsync({ nodeId: node.id, input })}
      />

      {vnc ? (
        <VncSection
          node={node}
          open={openSection === 'vnc'}
          onToggle={toggle('vnc')}
          canConnect={vnc.canConnect(node.id)}
          canWrite={vnc.canWrite}
          canManageCredentials={vnc.canManageCredentials}
          onConnect={vnc.onConnect}
        />
      ) : null}

      {ssh ? (
        <SshSection
          node={node}
          open={openSection === 'ssh'}
          onToggle={toggle('ssh')}
          canConnect={ssh.canConnect(node.id)}
          canWrite={ssh.canWrite}
          canManageCredentials={ssh.canManageCredentials}
          onConnect={ssh.onConnect}
        />
      ) : null}

      {video ? (
        <CameraSection
          node={node}
          open={openSection === 'camera'}
          onToggle={toggle('camera')}
          canView={video.canView}
          canWrite={video.canWrite}
          canArchive={video.canArchive}
          onOpen={video.onOpen}
          onOpenArchive={video.onOpenArchive}
        />
      ) : null}

      {video ? (
        <RecorderSection
          node={node}
          open={openSection === 'recorder'}
          onToggle={toggle('recorder')}
          canView={video.canView}
          canWrite={video.canWrite}
          canArchive={video.canArchive}
          onOpen={video.onOpen}
          onOpenArchive={video.onOpenArchive}
        />
      ) : null}

      <AddressesSection node={node} addresses={addresses} live={live} canWrite={canWrite} />

      <MonitorsSection node={node} live={live} canWrite={canWrite} />
    </aside>
  );
}
